import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, UserCog, Pencil, Eye, EyeOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_LABEL = { admin: "Administrador", abogado: "Abogado", colaborador: "Colaborador" };

export default function UsersPage() {
  const { user: me, refresh } = useAuth();
  const [items, setItems] = useState([]);

  // Create
  const emptyCreate = { email: "", password: "", name: "", role: "abogado" };
  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [showCreatePassword, setShowCreatePassword] = useState(false);

  // Edit
  const [editing, setEditing] = useState(null); // user object or null
  const [editForm, setEditForm] = useState({ email: "", name: "", role: "abogado", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const genPassword = () => {
    // Password legible, 12 chars, letras+números, sin caracteres ambiguos (0OIl1)
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const arr = new Uint32Array(12);
    (window.crypto || window.msCrypto).getRandomValues(arr);
    return Array.from(arr, (n) => chars[n % chars.length]).join("");
  };

  const load = async () => {
    try { const { data } = await api.get("/users"); setItems(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", createForm);
      toast.success("Usuario creado");
      setOpenCreate(false); setCreateForm(emptyCreate); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ email: u.email, name: u.name, role: u.role, password: "" });
    setShowPassword(false);
  };

  const saveEdit = async () => {
    try {
      const payload = {
        email: editForm.email.trim() !== editing.email ? editForm.email.trim() : undefined,
        name: editForm.name.trim() !== editing.name ? editForm.name.trim() : undefined,
        role: editForm.role !== editing.role ? editForm.role : undefined,
        password: editForm.password ? editForm.password : undefined,
      };
      // No-op guard
      if (!Object.values(payload).some(v => v !== undefined)) {
        toast.info("Sin cambios"); setEditing(null); return;
      }
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("Usuario actualizado");
      // Si se auto-editó, refrescar contexto de auth (email/name en cabecera)
      if (editing.id === me.id && (payload.email || payload.name)) {
        await refresh?.();
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Usuarios"
        subtitle="Gestión del equipo: abogados, colaboradores y administradores."
        actions={
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button data-testid="new-user-button" className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]">
                <Plus className="w-4 h-4 mr-2" />Nuevo usuario
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-md" data-testid="user-create-dialog">
              <DialogHeader><DialogTitle className="font-display text-2xl">Nuevo usuario</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase tracking-wider">Nombre</Label>
                  <Input data-testid="user-create-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Email</Label>
                  <Input data-testid="user-create-email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="rounded-sm mt-1" /></div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Contraseña</Label>
                  <div className="relative mt-1">
                    <Input
                      data-testid="user-create-password"
                      type={showCreatePassword ? "text" : "password"}
                      value={createForm.password}
                      onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                      className="rounded-sm pr-20 font-mono tracking-wide"
                    />
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => setShowCreatePassword((v) => !v)}
                        className="p-1.5 text-neutral-500 hover:text-[#7B1826]"
                        data-testid="user-create-password-toggle"
                      >
                        {showCreatePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setCreateForm({ ...createForm, password: genPassword() }); setShowCreatePassword(true); }}
                        className="p-1.5 text-neutral-500 hover:text-[#7B1826]"
                        data-testid="user-create-password-generate"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider">Rol</Label>
                  <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="user-create-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="abogado">Abogado</SelectItem>
                      <SelectItem value="colaborador">Colaborador / Procurador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenCreate(false)} className="rounded-sm">Cancelar</Button>
                <Button onClick={create} data-testid="user-create-button" disabled={!createForm.email || !createForm.password || !createForm.name} className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]">
                  Crear
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E5E7EB] bg-white">
          <UserCog className="w-10 h-10 mx-auto text-neutral-400" />
          <p className="mt-4 text-sm text-neutral-500">Sin usuarios.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F3F4F6] text-[11px] uppercase tracking-wider text-neutral-600">
                <th className="text-left py-3 px-4">Nombre</th>
                <th className="text-left py-3 px-4">Email</th>
                <th className="text-left py-3 px-4">Rol</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-[#E5E7EB] hover:bg-[#FBFBF9]" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4 font-medium">{u.name}</td>
                  <td className="py-3 px-4 text-neutral-600">{u.email}</td>
                  <td className="py-3 px-4">
                    <Badge variant="outline" className="rounded-sm text-[10px] uppercase border-[#E5E7EB]">
                      {ROLE_LABEL[u.role] || u.role}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => openEdit(u)}
                        className="p-1.5 text-neutral-500 hover:text-[#7B1826]"
                        data-testid={`user-edit-${u.id}`}
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {u.id !== me.id && (
                        <button
                          onClick={() => remove(u.id)}
                          className="p-1.5 text-neutral-500 hover:text-[#DC2626]"
                          data-testid={`user-delete-${u.id}`}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Editar usuario</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Nombre</Label>
                <Input data-testid="user-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Email</Label>
                <Input data-testid="user-edit-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Nueva contraseña</Label>
                <div className="relative mt-1">
                  <Input
                    data-testid="user-edit-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Dejar vacío para no cambiarla"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    className="rounded-sm pr-20 font-mono tracking-wide"
                  />
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="p-1.5 text-neutral-500 hover:text-[#7B1826]"
                      data-testid="user-edit-password-toggle"
                      title={showPassword ? "Ocultar" : "Mostrar"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditForm({ ...editForm, password: genPassword() }); setShowPassword(true); }}
                      className="p-1.5 text-neutral-500 hover:text-[#7B1826]"
                      data-testid="user-edit-password-generate"
                      title="Generar contraseña segura"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-neutral-500 mt-1">
                  Mínimo 6 caracteres. Usa el icono 👁 para ver lo que escribes y ⟳ para generar una segura.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Rol</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                  disabled={editing.id === me.id}
                >
                  <SelectTrigger className="rounded-sm mt-1" data-testid="user-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="abogado">Abogado</SelectItem>
                    <SelectItem value="colaborador">Colaborador / Procurador</SelectItem>
                  </SelectContent>
                </Select>
                {editing.id === me.id && (
                  <p className="text-[11px] text-neutral-500 mt-1">No puedes cambiarte el rol a ti mismo.</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm">Cancelar</Button>
            <Button
              onClick={saveEdit}
              data-testid="user-edit-save-button"
              disabled={
                !editForm.email ||
                !editForm.name ||
                (editForm.password && editForm.password.length < 6)
              }
              className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
